
import { defineComponent } from 'vue'
import roleAccessService, { Permission } from '@/http/role-access'
import { createDefaultPermissions, getAvailablePaths, getPathLabel } from '@/utils/role-access-helper'

interface Role {
  role_id: number
  name: string
}

export default defineComponent({
  name: 'RoleAccessSection',
  data() {
    return {
      roles: [] as Role[],
      selectedRoleId: '' as string | number,
      permissions: [] as Permission[],
      originalPermissions: [] as Permission[],
      loading: false,
      saving: false,
      isModified: false,
      message: '',
      messageType: '' as 'success' | 'error'
    }
  },
  computed: {
    selectedRoleName(): string {
      const role = this.roles.find((r: Role) => r.role_id === this.selectedRoleId)
      return role?.name || ''
    },
    pathLabel(): (path: string) => string {
      return getPathLabel
    }
  },
  mounted() {
    this.loadRoles()
  },
  methods: {
    getPathLabel,
    async loadRoles() {
      try {
        const response = await roleAccessService.getRoles()
        
        // Handle different response formats
        let rolesData: Role[] = []
        if (response.data && Array.isArray(response.data)) {
          rolesData = response.data
        } else if (response.data && Array.isArray(response.data.roles)) {
          rolesData = response.data.roles
        } else if (Array.isArray(response)) {
          rolesData = response
        }
        
        this.roles = rolesData
      } catch (error) {
        console.error('Error loading roles:', error)
        this.showMessage('Error loading roles', 'error')
      }
    },
    async loadPermissions() {
      if (!this.selectedRoleId) {
        this.permissions = []
        this.originalPermissions = []
        return
      }

      this.loading = true
      try {
        const response = await roleAccessService.getPermissions(
          Number(this.selectedRoleId)
        )
        
        // Handle different response formats
        let permissionsData: Permission[] = []
        if (response.data && Array.isArray(response.data)) {
          permissionsData = response.data
        } else if (response.data && Array.isArray(response.data.permissions)) {
          permissionsData = response.data.permissions
        } else if (Array.isArray(response)) {
          permissionsData = response
        }
        
        // Jika permissions kosong atau tidak valid, gunakan default dari router
        if (permissionsData.length === 0) {
          console.warn('No permissions from backend, using default from router config')
          permissionsData = createDefaultPermissions()
        } else {
          // Merge dengan default paths untuk ensure semua paths ada
          const availablePaths = getAvailablePaths()
          const existingPaths = new Set(permissionsData.map(p => p.path))
          
          // Add missing paths dengan default value false
          const missingPaths = availablePaths.filter(p => !existingPaths.has(p))
          permissionsData = [
            ...permissionsData,
            ...missingPaths.map(path => ({
              path,
              create: false,
              read: false,
              update: false,
              delete: false
            }))
          ]
        }
        
        // Ensure all boolean fields are properly set
        permissionsData = permissionsData.map((p: any) => ({
          path: p.path || '',
          create: p.create === true || p.create === 'true',
          read: p.read === true || p.read === 'true',
          update: p.update === true || p.update === 'true',
          delete: p.delete === true || p.delete === 'true'
        }))
        
        this.permissions = permissionsData.sort((a, b) => a.path.localeCompare(b.path))
        this.originalPermissions = JSON.parse(JSON.stringify(this.permissions))
        this.isModified = false
        this.message = ''
      } catch (error) {
        console.error('Error loading permissions:', error)
        // Use default permissions sebagai fallback
        this.permissions = createDefaultPermissions()
        this.originalPermissions = JSON.parse(JSON.stringify(this.permissions))
        this.showMessage('Using default permissions from router config', 'error')
      } finally {
        this.loading = false
      }
    },
    async savePermissions() {
      if (!this.selectedRoleId) return

      this.saving = true
      try {
        await roleAccessService.updatePermissions(
          Number(this.selectedRoleId),
          this.permissions
        )
        this.originalPermissions = JSON.parse(JSON.stringify(this.permissions))
        this.isModified = false
        this.showMessage('Permissions saved successfully', 'success')
      } catch (error) {
        this.showMessage('Error saving permissions', 'error')
      } finally {
        this.saving = false
      }
    },
    resetPermissions() {
      this.permissions = JSON.parse(JSON.stringify(this.originalPermissions))
      this.isModified = false
      this.message = ''
    },
    selectAllPermissions() {
      this.permissions.forEach((p: Permission) => {
        p.create = true
        p.read = true
        p.update = true
        p.delete = true
      })
      this.markAsModified()
    },
    deselectAllPermissions() {
      this.permissions.forEach((p: Permission) => {
        p.create = false
        p.read = false
        p.update = false
        p.delete = false
      })
      this.markAsModified()
    },
    checkAllPerRow(index: number) {
      const permission = this.permissions[index]
      const isAllChecked = permission.create && permission.read && permission.update && permission.delete
      
      if (isAllChecked) {
        // Jika semua sudah checked, uncheck semua
        permission.create = false
        permission.read = false
        permission.update = false
        permission.delete = false
      } else {
        // Jika tidak semua checked, check semua
        permission.create = true
        permission.read = true
        permission.update = true
        permission.delete = true
      }
      this.markAsModified()
    },
    isAllCheckedPerRow(index: number): boolean {
      const permission = this.permissions[index]
      return permission.create && permission.read && permission.update && permission.delete
    },
    uncheckAllPerRow(index: number) {
      const permission = this.permissions[index]
      permission.create = false
      permission.read = false
      permission.update = false
      permission.delete = false
      this.markAsModified()
    },
    markAsModified() {
      this.isModified = JSON.stringify(this.permissions) !== 
                       JSON.stringify(this.originalPermissions)
    },
    showMessage(msg: string, type: 'success' | 'error') {
      this.message = msg
      this.messageType = type
      setTimeout(() => {
        this.message = ''
      }, 5000)
    }
  }
})
